import { createSlice } from '@reduxjs/toolkit';
import instance from '../../axios/ctdentAxios';
import { DIAGNOCAT_PATIENTS } from '../../assets/conf/api-endpoints';
import {
	deleteAnalysis,
	deletePatient,
	deleteStudy,
	fetchDoctorsLink,
	fetchPatientStudiesWithImagesAndAnalyses,
	requestStudyAnalysis,
} from '../../services/diagnocat/diagnocat.service';
import {
	getAllRadiologistPatients,
	getAllRadiologists,
	getPdfTypes,
	updateAppointmentLink,
} from '../../services/diagnocat/diagnocat-ctdent.service';
import ctdentInstance from '../../axios/ctdentAxios';
import {
	getAppointmentByPatientId,
	updateAppointment,
} from '../../services/doctors/appointment.service';

const diagnocatInitialState = {
	patients: [],
	//TODO: convert 'loading' into object, with loading value for each action
	loading: 'idle',
	patientsLoading: 'idle',
	studiesLoading: 'idle',
	error: {},
	selectedPatient: {},
	selectedPatientStudies: [],
	selectedPatientAppointment: {},
	pdfTypes: [],
	radiologists: [],
};

export const diagnocatSlice = createSlice({
	name: 'diagnocat',
	initialState: diagnocatInitialState,
	reducers: {
		// standard reducer logic, with auto-generated action types per reducer
		fetchAllPatientsLoading(state, action) {
			if (state.patientsLoading === 'idle') {
				state.patientsLoading = 'pending';
			}
		},
		fetchAllPatientsLoadingSucceed(state, action) {
			state.patientsLoading = 'idle';
			state.patients = action.payload;
		},
		fetchAllPatientsLoadingFailed(state, action) {
			if (state.patientsLoading === 'pending') {
				state.patientsLoading = 'idle';
				state.error = action.payload;
			}
		},

		createPatientLoading(state, action) {
			if (state.loading === 'idle') {
				state.loading = 'pending';
			}
		},
		createPatientSucceed(state, action) {
			if (state.loading === 'pending') {
				state.loading = 'idle';

				const newPatient = {
					id: action.payload.diagnocat_uid,
					patientId: action.payload.id.toString(),
					lastName: action.payload.last_name,
					firstName: action.payload.first_name,
					dateOfBirth: action.payload.dob,
					gender: action.payload.gender,
				};
				state.patients = state.patients.concat(newPatient);
				state.selectedPatient = newPatient;
			}
		},
		createPatientFailed(state, action) {
			if (state.loading === 'pending') {
				state.loading = 'idle';
				state.error = action.payload;
			}
		},

		deletePatientLoading(state, action) {
			if (state.loading === 'idle') {
				state.loading = 'pending';
			}
		},
		deletePatientSucceed(state, action) {
			if (state.loading === 'pending') {
				state.loading = 'idle';
				state.patients = state.patients.filter(
					(patient) => patient.id !== action.payload,
				);
			}
		},
		deletePatientFailed(state, action) {
			if (state.loading === 'pending') {
				state.loading = 'idle';
				state.error = action.payload;
			}
		},

		deleteStudyLoading(state, action) {
			if (state.loading === 'idle') {
				state.loading = 'pending';
			}
		},
		deleteStudySucceed(state, action) {
			if (state.loading === 'pending') {
				state.loading = 'idle';
				state.selectedPatientStudies = state.selectedPatientStudies.filter(
					(study) => study.uid !== action.payload,
				);
			}
		},
		deleteStudyFailed(state, action) {
			if (state.loading === 'pending') {
				state.loading = 'idle';
				state.error = action.payload;
			}
		},

		deleteAnalysisLoading(state, action) {
			if (state.studiesLoading === 'idle') {
				state.studiesLoading = 'pending';
			}
		},
		deleteAnalysisSucceed(state, action) {
			if (state.studiesLoading === 'pending') {
				return {
					...state,
					studiesLoading: 'idle',
					selectedPatientStudies: state.selectedPatientStudies.map((study) =>
						study.uid === action.payload.studyUID
							? // transform the one with a matching study id
							  {
									...study,
									analyses: study.analyses.filter(
										(analysis) => analysis.uid !== action.payload.analysisUID,
									),
							  }
							: // otherwise return original study
							  study,
					),
				};
			}
		},
		deleteAnalysisFailed(state, action) {
			if (state.studiesLoading === 'pending') {
				state.studiesLoading = 'idle';
				state.error = action.payload;
			}
		},

		loadPatientStudiesWithImagesAndAnalysesLoading(state, action) {
			if (state.studiesLoading === 'idle') {
				state.studiesLoading = 'pending';
			}
		},
		loadPatientStudiesWithImagesAndAnalysesSucceed(state, action) {
			state.selectedPatientStudies = action.payload;
			state.studiesLoading = 'idle';
		},

		loadPatientStudiesWithImagesAndAnalysesFailed(state, action) {
			if (state.studiesLoading === 'pending') {
				state.studiesLoading = 'idle';
				state.error = action.payload;
			}
		},

		fetchTypesLoading(state, action) {
			if (state.loading === 'idle') {
				state.loading = 'pending';
			}
		},
		fetchTypesSucceed(state, action) {
			state.pdfTypes = action.payload;
			state.loading = 'idle';
		},

		fetchTypesFailed(state, action) {
			if (state.loading === 'pending') {
				state.loading = 'idle';
				state.error = action.payload;
			}
		},

		requestAnalysisSucceed(state, action) {
			return {
				...state,
				selectedPatientStudies: state.selectedPatientStudies.map((study) =>
					study.uid === action.payload.study_uid
						? // transform the one with a matching study id
						  { ...study, analyses: study.analyses.concat(action.payload) }
						: // otherwise return original study
						  study,
				),
			};
		},

		setSelectedPatient(state, action) {
			const patient = state.patients.find(
				(patient) => patient.patientId === action.payload,
			);
			state.selectedPatient = patient;
		},

		clearSelectedPatient(state, action) {
			state.selectedPatient = null;
			state.selectedPatientStudies = null;
			state.selectedPatientAppointment = null;
		},

		createDoctorsLinkLoading(state, action) {
			if (state.loading === 'idle') {
				state.loading = 'pending';
			}
		},
		createDoctorsLinkSucceed(state, action) {
			state.selectedPatientAppointment = {
				...state.selectedPatientAppointment,
				diagnocat_link: action.payload,
			};
			state.loading = 'idle';
		},
		createDoctorsLinkFailed(state, action) {
			if (state.loading === 'pending') {
				state.loading = 'idle';
				state.error = action.payload;
			}
		},

		loadAppointmentByPatientIdLoading(state, action) {
			if (state.loading === 'idle') {
				state.loading = 'pending';
			}
		},
		loadAppointmentByPatientIdSucceed(state, action) {
			state.selectedPatientAppointment = action.payload;
			state.loading = 'idle';
		},
		loadAppointmentByPatientIdFailed(state, action) {
			if (state.loading === 'pending') {
				state.loading = 'idle';
				state.error = action.payload;
			}
		},

		updateAppointmentStatusLoading(state, action) {
			if (state.loading === 'idle') {
				state.loading = 'pending';
			}
		},
		updateAppointmentStatusSucceed(state, action) {
			state.selectedPatientAppointment = {
				...state.selectedPatientAppointment,
				diagnocat_status: action.payload,
			};
			state.loading = 'idle';
		},
		updateAppointmentStatusFailed(state, action) {
			if (state.loading === 'pending') {
				state.loading = 'idle';
				state.error = action.payload;
			}
		},

		fetchRadiologistsLoading(state, action) {
			if (state.loading === 'idle') {
				state.loading = 'pending';
			}
		},
		fetchRadiologistsSucceed(state, action) {
			state.radiologists = action.payload;
			state.loading = 'idle';
		},
		fetchRadiologistsFailed(state, action) {
			if (state.loading === 'pending') {
				state.loading = 'idle';
				state.error = action.payload;
			}
		},
	},
});

// Destructure and export the plain action creators
export const {
	fetchAllPatientsLoading,
	fetchAllPatientsLoadingSucceed,
	fetchAllPatientsLoadingFailed,
	createPatientLoading,
	createPatientSucceed,
	createPatientFailed,
	deletePatientLoading,
	deletePatientSucceed,
	deletePatientFailed,
	deleteStudyLoading,
	deleteStudySucceed,
	deleteStudyFailed,
	deleteAnalysisLoading,
	deleteAnalysisSucceed,
	deleteAnalysisFailed,
	loadPatientStudiesWithImagesAndAnalysesLoading,
	loadPatientStudiesWithImagesAndAnalysesSucceed,
	loadPatientStudiesWithImagesAndAnalysesFailed,
	createDoctorsLinkLoading,
	createDoctorsLinkSucceed,
	createDoctorsLinkFailed,
	clearSelectedPatient,
	setSelectedPatient,
	requestAnalysisSucceed,
	fetchTypesLoading,
	fetchTypesSucceed,
	fetchTypesFailed,
	loadAppointmentByPatientIdLoading,
	loadAppointmentByPatientIdSucceed,
	loadAppointmentByPatientIdFailed,
	updateAppointmentStatusLoading,
	updateAppointmentStatusSucceed,
	updateAppointmentStatusFailed,
	fetchRadiologistsLoading,
	fetchRadiologistsSucceed,
	fetchRadiologistsFailed,
} = diagnocatSlice.actions;

/**
 * REDUX THUNKS
 * Thunks dispatch action creators
 * */
export const fetchAllPatients = () => async (dispatch) => {
	dispatch(fetchAllPatientsLoading());
	try {
		const response = await instance.get(DIAGNOCAT_PATIENTS);
		let patients = response.data.map((patient) => ({
			id: patient.uid,
			patientId: patient.patient_id,
			lastName: patient.name_part1,
			firstName: patient.name_part2,
			dateOfBirth: patient.date_of_birth,
			gender: patient.gender,
			referringDoctor: patient.attributes.referringDoctor,
		}));
		dispatch(fetchAllPatientsLoadingSucceed(patients));
	} catch (error) {
		dispatch(fetchAllPatientsLoadingFailed(error));
	}
};

export const fetchAllRadiologistPatients =
	(radiologistId) => async (dispatch) => {
		dispatch(fetchAllPatientsLoading());
		try {
			let patients = await getAllRadiologistPatients(radiologistId);
			dispatch(fetchAllPatientsLoadingSucceed(patients));
		} catch (error) {
			dispatch(fetchAllPatientsLoadingFailed(error));
		}
	};

export const createPatient = (patientData) => async (dispatch) => {
	dispatch(createPatientLoading());
	try {
		// const response = await instance.post(DIAGNOCAT_PATIENTS, patientData);
		let response = await ctdentInstance.post(
			'diagnocat/appointment/createpatient',
			patientData,
		);
		const patientId = response.data.data.id;
		dispatch(setSelectedPatient(patientId));
		dispatch(createPatientSucceed(response.data.data));
	} catch (error) {
		dispatch(createPatientFailed(error));
	}
};

export const deletePatientState = (patientId) => async (dispatch) => {
	dispatch(deletePatientLoading());
	try {
		const response = await deletePatient(patientId);
		dispatch(deletePatientSucceed(patientId));
	} catch (error) {
		dispatch(deletePatientFailed(error));
	}
};

export const deleteStudyState = (studyId) => async (dispatch) => {
	dispatch(deleteStudyLoading());
	try {
		const response = await deleteStudy(studyId);
		// dispatch(deleteStudySucceed(response.data));
		dispatch(deleteStudySucceed(studyId));
	} catch (error) {
		dispatch(deleteStudyFailed(error));
	}
};

export const deleteAnalysisState =
	(studyUID, analysisUID) => async (dispatch) => {
		dispatch(deleteAnalysisLoading());
		try {
			const response = await deleteAnalysis(analysisUID);
			dispatch(deleteAnalysisSucceed({ studyUID, analysisUID }));
		} catch (error) {
			dispatch(deleteAnalysisFailed(error));
		}
	};

export const sendLinkToDoctor =
	(patientUID, doctorEmail, appointmentId) => async (dispatch) => {
		dispatch(createDoctorsLinkLoading());
		try {
			//create Doctors Link (Diagnocat API)
			const diagnocatResponse = await fetchDoctorsLink(patientUID, doctorEmail);

			//save to ct-dent db
			const response1 = await updateAppointmentLink(
				appointmentId,
				diagnocatResponse.link,
			);
			// const response2 = await updateAppointmentPdfLinks(appointmentId, pdfLinks);

			dispatch(createDoctorsLinkSucceed(diagnocatResponse.link));
		} catch (error) {
			dispatch(createDoctorsLinkFailed(error));
		}
	};

/**
 * loads selected patient and studies(including images and analyses)
 */
export const loadPatientStudiesWithImagesAndAnalyses =
	(patientId) => async (dispatch) => {
		dispatch(loadPatientStudiesWithImagesAndAnalysesLoading());
		try {
			const patientStudiesWithImagesAndAnalyses =
				await fetchPatientStudiesWithImagesAndAnalyses(patientId);
			dispatch(
				loadPatientStudiesWithImagesAndAnalysesSucceed(
					patientStudiesWithImagesAndAnalyses,
				),
			);
		} catch (error) {
			dispatch(loadPatientStudiesWithImagesAndAnalysesFailed(error));
		}
	};

export const loadAppointmentByPatientId = (patientId) => async (dispatch) => {
	dispatch(loadAppointmentByPatientIdLoading());
	try {
		const appointment = await getAppointmentByPatientId(patientId);
		dispatch(loadAppointmentByPatientIdSucceed(appointment));
	} catch (error) {
		dispatch(loadAppointmentByPatientIdFailed(error));
	}
};

export const requestAnalysis =
	(studyId, analysisRequest) => async (dispatch) => {
		const requestedAnalysis = await requestStudyAnalysis(
			studyId,
			analysisRequest,
		);
		dispatch(requestAnalysisSucceed(requestedAnalysis, studyId));
	};

export const fetchTypes = () => async (dispatch) => {
	dispatch(fetchTypesLoading());
	try {
		const types = await getPdfTypes();
		dispatch(fetchTypesSucceed(types.data));
	} catch (error) {
		dispatch(fetchTypesFailed(error));
	}
};

export const updateAppointmentStatus =
	(appointmentId, fieldValue) => async (dispatch) => {
		dispatch(updateAppointmentStatusLoading());
		try {
			const res = await updateAppointment(
				appointmentId,
				'diagnocat_status',
				fieldValue,
			);
			dispatch(updateAppointmentStatusSucceed(res.data.value));
		} catch (error) {
			dispatch(updateAppointmentStatusFailed(error));
		}
	};

export const fetchRadiologists = () => async (dispatch) => {
	dispatch(fetchRadiologistsLoading());
	try {
		const res = await getAllRadiologists();
		dispatch(fetchRadiologistsSucceed(res.data));
	} catch (error) {
		dispatch(fetchRadiologistsFailed(error));
	}
};

export const selectPatients = (state) => state.diagnocat.patients;
export const selectLoading = (state) => state.diagnocat.loading;
export const selectPatientsLoading = (state) => state.diagnocat.patientsLoading;
export const selectStudiesLoading = (state) => state.diagnocat.studiesLoading;
export const selectStudies = (state) => state.diagnocat.selectedPatientStudies;
export const selectPatient = (state) => state.diagnocat.selectedPatient;
export const selectAppointment = (state) =>
	state.diagnocat.selectedPatientAppointment;
export const selectTypes = (state) => state.diagnocat.pdfTypes;
export const selectRadiologists = (state) => state.diagnocat.radiologists;

export default diagnocatSlice.reducer;
